/**
 * @file Attachment deep-link opening helpers.
 */

import {
    getAttachmentCurrentVersion,
    normalizeAttachmentRecords,
} from "../../shared/attachments/attachmentModel.js";
import { openAttachmentPreview } from "../../shared/attachments/attachmentPreview.js";
import { getAttachmentOfficePreviewCallable } from "../firebase/callables.js";
import { appData, appDataDetails } from "../state/state.js";
import { showSystemToast } from "../../shared/ui/notifications.js";

/**
 * Finds a loaded item for an attachment route.
 * @param {object} routeTarget Parsed detail route target.
 * @returns {object|null} Loaded item.
 */
function findLoadedRouteItem(routeTarget) {
    if (routeTarget.itemType === "task") {
        return appData.tasks.find((task) => task.id === routeTarget.itemId) ||
            appDataDetails.tasks.find((task) => task.id === routeTarget.itemId) ||
            null;
    }

    const collection = appData[routeTarget.collectionName];
    if (!Array.isArray(collection)) {return null;}
    return collection.find((item) => item.id === routeTarget.itemId) || null;
}

/**
 * Opens an attachment referenced by a resolved detail route.
 * @param {object} routeTarget Parsed detail route target.
 * @returns {Promise<boolean>} True when an attachment was opened.
 */
export async function openAttachmentRouteTarget(routeTarget) {
    if (!routeTarget?.attachmentId) {return false;}

    const item = findLoadedRouteItem(routeTarget);
    const attachment = normalizeAttachmentRecords(item?.attachments || [])
        .find((entry) => entry.id === routeTarget.attachmentId);
    if (!attachment) {
        showSystemToast("That attachment is no longer available.", {
            id: "attachment-route-missing",
        });
        return false;
    }

    const version = routeTarget.versionId ?
        attachment.versions.find((entry) => entry.id === routeTarget.versionId) :
        getAttachmentCurrentVersion(attachment);
    if (!version) {
        showSystemToast("That attachment version is no longer available.", {
            id: "attachment-route-version-missing",
        });
        return false;
    }

    await openAttachmentPreview({
        attachment: {
            ...attachment,
            currentVersionId: version.id,
            currentVersion: version,
        },
        handleAttachmentOfficePreview: async () => {
            const result = await getAttachmentOfficePreviewCallable({
                itemId: routeTarget.itemId,
                itemType: routeTarget.itemType,
                attachmentId: attachment.id,
                versionId: version.id,
                filePath: version.path,
            });
            return result.data;
        },
    });

    return true;
}
